import React, { type ReactNode } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Loader } from '@vibe/core'
import { useAuth } from '../../contexts/AuthContext'
import { useRoutePermissions } from '../../hooks/usePermissions'
import { UserRole, PermissionResource, PermissionAction } from '../../types'

interface RouteGuardProps {
  children: ReactNode
  requireAuth?: boolean
  requiredRole?: UserRole
  requiredPermission?: {
    resource: PermissionResource
    action: PermissionAction
  }
  fallbackPath?: string
  fallbackComponent?: ReactNode
}

/**
 * RouteGuard component for protecting routes with role and permission checks
 * Extends AuthGuard functionality with granular permission control
 */
export const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  requireAuth = true,
  requiredRole,
  requiredPermission,
  fallbackPath = '/dashboard',
  fallbackComponent,
}) => {
  const { isAuthenticated, isLoading, user, hasPermission, hasRole } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader size="large" />
      </div>
    )
  }

  // Handle login page access
  if (!requireAuth) {
    if (isAuthenticated) {
      // Redirect authenticated users away from login
      const from = location.state?.from?.pathname || '/dashboard'
      return <Navigate to={from} replace />
    }
    return <>{children}</>
  }

  // Handle protected routes - check authentication first
  if (!isAuthenticated) {
    // Save the attempted location for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check role requirement
  if (requiredRole && !hasRole(requiredRole)) {
    if (fallbackComponent) {
      return <>{fallbackComponent}</>
    }
    return <Navigate to={fallbackPath} replace />
  }

  // Check permission requirement
  if (requiredPermission) {
    const { resource, action } = requiredPermission
    if (!hasPermission(resource, action)) {
      if (fallbackComponent) {
        return <>{fallbackComponent}</>
      }
      return <Navigate to={fallbackPath} replace />
    }
  }

  return <>{children}</>
}

/**
 * Higher-order component for protecting routes with permissions
 */
export function withRouteGuard<P extends object>(
  Component: React.ComponentType<P>,
  guardProps: Omit<RouteGuardProps, 'children'>
) {
  return function GuardedComponent(props: P) {
    return (
      <RouteGuard {...guardProps}>
        <Component {...props} />
      </RouteGuard>
    )
  }
}

/**
 * Unauthorized access component
 */
export const UnauthorizedAccess: React.FC<{
  message?: string
  showBackButton?: boolean
}> = ({
  message = 'No tienes permisos para acceder a esta página',
  showBackButton = true,
}) => {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center max-w-md mx-auto p-6">
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Acceso No Autorizado
          </h1>
          <p className="text-gray-600 mb-6">{message}</p>
        </div>

        {showBackButton && (
          <div className="space-y-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Ir al Dashboard
            </button>
            <button
              onClick={() => navigate(-1)}
              className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
            >
              Volver
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Convenience components for common route protections
 */
export const AdminRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <RouteGuard
    requiredRole={UserRole.ADMINISTRADOR}
    fallbackComponent={
      <UnauthorizedAccess message="Solo los administradores pueden acceder a esta página" />
    }
  >
    {children}
  </RouteGuard>
)

export const EditorRoute: React.FC<{ children: ReactNode }> = ({
  children,
}) => (
  <RouteGuard
    requiredRole={UserRole.EDITOR}
    fallbackComponent={
      <UnauthorizedAccess message="Necesitas permisos de editor para acceder a esta página" />
    }
  >
    {children}
  </RouteGuard>
)

export const UserManagementRoute: React.FC<{ children: ReactNode }> = ({
  children,
}) => (
  <RouteGuard
    requiredPermission={{
      resource: PermissionResource.USERS,
      action: PermissionAction.READ,
    }}
    fallbackComponent={
      <UnauthorizedAccess message="No tienes permisos para gestionar usuarios" />
    }
  >
    {children}
  </RouteGuard>
)

export const RoleManagementRoute: React.FC<{ children: ReactNode }> = ({
  children,
}) => (
  <RouteGuard
    requiredPermission={{
      resource: PermissionResource.ROLES,
      action: PermissionAction.READ,
    }}
    fallbackComponent={
      <UnauthorizedAccess message="No tienes permisos para gestionar roles" />
    }
  >
    {children}
  </RouteGuard>
)

export const JobDefinitionManagementRoute: React.FC<{
  children: ReactNode
}> = ({ children }) => (
  <RouteGuard
    requiredPermission={{
      resource: PermissionResource.SOLICITUDES,
      action: PermissionAction.CREATE,
    }}
    fallbackComponent={
      <UnauthorizedAccess message="No tienes permisos para gestionar definiciones de trabajo" />
    }
  >
    {children}
  </RouteGuard>
)
